import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { HeartIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
import { IconButton } from '../../../components/ui/icon-button';
import { Link } from '../../../components/ui/Link';
import { buttonVariants } from '../../../components/ui/button-variants';
import { cn } from '../../../lib/utils';
import type { ShelterAnimal } from '../types';
import { MatchPercentBadge } from './MatchPercentBadge';

export type MatchResultPetCardProps = {
  animal: ShelterAnimal;
  matchPercent: number;
  profileHref: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  className?: string;
};

export function MatchResultPetCard({
  animal,
  matchPercent,
  profileHref,
  isFavorite,
  onToggleFavorite,
  className,
}: MatchResultPetCardProps) {
  const router = useRouter();
  const photoAlt = animal.Name ? `${animal.Name} photo` : 'Pet photo';
  const isExternalProfile = /^https?:\/\//i.test(profileHref);
  const buttonClass = cn(
    buttonVariants({ variant: 'primary', size: 'md', fullWidth: true }),
    'mt-auto no-underline',
  );

  const openProfile = () => {
    if (isExternalProfile) {
      window.open(profileHref, '_blank', 'noopener,noreferrer');
      return;
    }
    router.push(profileHref);
  };

  const profileCta = isExternalProfile ? (
    <a
      href={profileHref}
      target='_blank'
      rel='noopener noreferrer'
      className={buttonClass}
      onClick={(e) => e.stopPropagation()}
    >
      View profile
      <ArrowRightIcon className='size-4' aria-hidden />
    </a>
  ) : (
    <Link href={profileHref} className={buttonClass} onClick={(e) => e.stopPropagation()}>
      View profile
      <ArrowRightIcon className='size-4' aria-hidden />
    </Link>
  );

  return (
    <article
      className={cn(
        'flex h-full min-h-0 w-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
        className,
      )}
      role='link'
      tabIndex={0}
      aria-label={`View profile for ${animal.Name}`}
      onClick={openProfile}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openProfile();
        }
      }}
    >
      {/* Square media slot so every card shares the same geometry in the grid. */}
      <div className='relative aspect-square w-full shrink-0 bg-accent'>
        {animal.CoverPhoto ? (
          // eslint-disable-next-line @next/next/no-img-element -- shelter CDN URLs
          <img
            src={animal.CoverPhoto}
            alt={photoAlt}
            className='absolute inset-0 size-full object-cover'
          />
        ) : (
          <div
            className='flex size-full items-center justify-center text-sm text-muted-foreground'
            role='img'
            aria-label='No photo available'
          >
            No photo
          </div>
        )}
        <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-foreground/25 via-transparent to-transparent' />
        <div className='absolute right-2 top-2'>
          <MatchPercentBadge matchPercent={matchPercent} />
        </div>
        <div className='absolute bottom-2 right-2'>
          <IconButton
            type='button'
            variant='ghost'
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            aria-pressed={isFavorite}
            aria-label={
              isFavorite ? `Remove ${animal.Name} from favorites` : 'Favorite'
            }
            className={cn(
              'pointer-events-auto rounded-full border-0 bg-foreground/55 text-primary-foreground shadow-none backdrop-blur-sm hover:bg-foreground/65',
            )}
          >
            <HeartIcon
              className={cn(
                'size-5 text-primary-foreground',
                isFavorite && 'fill-primary-600 text-primary-600',
              )}
              aria-hidden
            />
          </IconButton>
        </div>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-3 p-4'>
        <h3 className='line-clamp-2 min-h-14 font-heading text-lg font-bold leading-snug text-card-foreground'>
          {animal.Name}
        </h3>
        {profileCta}
      </div>
    </article>
  );
}
